/**
 * LYT Communications - Restoration Tracker
 * Version: 1.0.0
 * Created: 2026-02-23
 *
 * Track surface restoration after splicing — asphalt, concrete, grass/sod.
 * Before/after photos, area/depth, status tracking.
 * Requires splicing QC approved before restoration can begin.
 */

import React, { useState, useCallback } from 'react';
import { HardHat, Camera, CheckCircle, Clock, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

const SURFACE_TYPES = [
  { id: 'asphalt', label: 'Asphalt', icon: '🛣️', codes: ['PA01', 'PA02', 'PA02A'] },
  { id: 'concrete', label: 'Concrete', icon: '🏗️', codes: ['PC01', 'PC02', 'PC02A'] },
  { id: 'grass', label: 'Grass / Sod', icon: '🌱', codes: ['RA1'] },
  { id: 'gravel', label: 'Gravel', icon: '🪨', codes: ['RC1'] },
];

const RESTORATION_STATUSES = ['Not Started', 'In Progress', 'Complete', 'QC Approved'];

function RestorationTracker({ segment, darkMode, user, onStatusUpdate }) {
  const [expanded, setExpanded] = useState(true);
  const [form, setForm] = useState({
    surface_type: '',
    area_sqft: '',
    depth_inches: '',
    notes: '',
  });

  const isAdmin = user?.role === 'Admin' || user?.role === 'admin' || user?.role === 'Supervisor';
  const splicingApproved = segment?.splicing_status === 'QC Approved';
  const restorationStatus = segment?.restoration_status || 'Not Started';

  const cardBg = darkMode ? '#112240' : '#f8fafc';
  const borderColor = darkMode ? '#1e3a5f' : '#e2e8f0';
  const text = darkMode ? '#ffffff' : '#1e293b';
  const textMuted = darkMode ? '#8892b0' : '#64748b';
  const accent = darkMode ? '#c850c0' : '#0077B6';
  const inputBg = darkMode ? '#0d1b2a' : '#ffffff';

  const STATUS_COLORS = {
    'Not Started': '#9E9E9E',
    'In Progress': '#FFB800',
    'Complete': '#4CAF50',
    'QC Approved': '#2196F3',
  };

  const handleStatusChange = useCallback((newStatus) => {
    onStatusUpdate?.('restoration', newStatus, {
      surface_type: form.surface_type,
      area_sqft: form.area_sqft,
      depth_inches: form.depth_inches,
      notes: form.notes,
    });
  }, [form, onStatusUpdate]);

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: `1px solid ${borderColor}`, background: inputBg, color: text,
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };

  const canStartRestoration = splicingApproved;

  return (
    <div style={{ background: cardBg, border: `1px solid ${borderColor}`, borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
      <div onClick={() => setExpanded(!expanded)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', cursor: 'pointer', borderBottom: expanded ? `1px solid ${borderColor}` : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <HardHat size={16} color={accent} />
          <span style={{ fontWeight: 700, fontSize: 14, color: text }}>Restoration</span>
          <span style={{
            fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
            background: `${STATUS_COLORS[restorationStatus]}20`,
            color: STATUS_COLORS[restorationStatus],
          }}>{restorationStatus}</span>
        </div>
        {expanded ? <ChevronUp size={14} color={textMuted} /> : <ChevronDown size={14} color={textMuted} />}
      </div>

      {expanded && (
        <div style={{ padding: '10px 14px' }}>
          {!canStartRestoration && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, background: '#FF980015', border: '1px solid #FF980030', marginBottom: 10 }}>
              <AlertTriangle size={14} color="#FF9800" />
              <span style={{ fontSize: 12, color: '#FF9800' }}>Splicing must be QC Approved before restoration can begin.</span>
            </div>
          )}

          {canStartRestoration && (
            <>
              {/* Surface type selection */}
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 4 }}>Surface Type</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                  {SURFACE_TYPES.map(st => (
                    <button key={st.id} onClick={() => setForm({ ...form, surface_type: st.id })}
                      style={{
                        padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                        border: `1px solid ${form.surface_type === st.id ? accent : borderColor}`,
                        background: form.surface_type === st.id ? `${accent}15` : 'transparent',
                        color: form.surface_type === st.id ? accent : text,
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                      <span>{st.icon}</span> {st.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Area and depth */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div>
                  <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Area (sq ft)</label>
                  <input type="number" value={form.area_sqft} onChange={e => setForm({ ...form, area_sqft: e.target.value })} placeholder="0" style={inputStyle} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Depth (inches)</label>
                  <input type="number" value={form.depth_inches} onChange={e => setForm({ ...form, depth_inches: e.target.value })} placeholder="0" style={inputStyle} />
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: 10 }}>
                <label style={{ fontSize: 11, color: textMuted, display: 'block', marginBottom: 3 }}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Restoration details..." rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
              </div>

              {/* Status actions */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {restorationStatus === 'Not Started' && (
                  <button onClick={() => handleStatusChange('In Progress')} disabled={!form.surface_type}
                    style={{ flex: 1, padding: '8px', fontSize: 12, fontWeight: 600, background: form.surface_type ? '#FFB800' : '#9E9E9E', color: '#fff', border: 'none', borderRadius: 6, cursor: form.surface_type ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <Clock size={12} /> Start Restoration
                  </button>
                )}
                {restorationStatus === 'In Progress' && (
                  <button onClick={() => handleStatusChange('Complete')}
                    style={{ flex: 1, padding: '8px', fontSize: 12, fontWeight: 600, background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <CheckCircle size={12} /> Mark Complete
                  </button>
                )}
                {restorationStatus === 'Complete' && isAdmin && (
                  <button onClick={() => handleStatusChange('QC Approved')}
                    style={{ flex: 1, padding: '8px', fontSize: 12, fontWeight: 600, background: '#2196F3', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <CheckCircle size={12} /> QC Approve
                  </button>
                )}
                {restorationStatus === 'QC Approved' && (
                  <div style={{ width: '100%', textAlign: 'center', padding: '10px', fontSize: 12, color: '#2196F3', fontWeight: 600 }}>
                    <CheckCircle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Restoration Complete
                  </div>
                )}
              </div>

              {/* Display existing restoration info from segment */}
              {segment?.restoration_surface && (
                <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 6, background: darkMode ? '#0d1b2a' : '#f0f9ff', border: `1px solid ${borderColor}`, fontSize: 11 }}>
                  <div style={{ color: textMuted, marginBottom: 4 }}>Recorded:</div>
                  <div style={{ color: text }}>
                    Surface: {segment.restoration_surface} | Area: {segment.restoration_area || '—'} sq ft | Depth: {segment.restoration_depth || '—'}"
                  </div>
                  {segment.restoration_notes && <div style={{ color: textMuted, fontStyle: 'italic', marginTop: 2 }}>{segment.restoration_notes}</div>}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default RestorationTracker;
